import { memo, useCallback, useContext, useEffect, useRef } from 'react';
import type { PlayerSpot } from '../tactics3d/formationProjection';
import { PitchZoomContext } from './PitchSvg';

/**
 * Draggable player roundel + jersey number for the 2D tactics board.
 *
 * Tokens are SVG circles in world metres (the parent SVG's viewBox is
 * `-W/2 .. +W/2` × `-L/2 .. +L/2`, so a circle at `(spot.x, spot.z)`
 * lands exactly on the same world position the 3D board would render.
 *
 * Drag projects pointer pixels back into world metres via the parent
 * SVG's `getScreenCTM`, which is the standard SVG idiom for converting
 * client coordinates into the SVG's user space. This works regardless
 * of how the SVG is sized in CSS — the CTM accounts for the responsive
 * scale.
 *
 * In `mode='draw'`, pointer-down on a token doesn't drag it — instead
 * it seeds an arrow at the token's position via `onStartArrowFromToken`,
 * mirroring the 3D board's "drag from a player to bind a run" gesture.
 *
 * Perf: during an active drag we mutate a `transform="translate(dx,dz)"`
 * attribute on a `<g>` wrapper via `useRef` and only commit the new
 * world position to React state on `pointerup`. That keeps the parent
 * (and the other 21 tokens) out of the render loop for every pointer
 * sample — `memo` alone only protects siblings, not the parent.
 */
export interface PlayerToken2DProps {
  spot: PlayerSpot;
  number: number;
  /** Optional short label (jersey number or initials) — when set,
   *  overrides `number` on the visible roundel. The bare `number`
   *  prop still drives the aria-label so screen readers can always
   *  refer to the slot by its index. */
  label?: string;
  team: 'home' | 'away';
  selected: boolean;
  /**
   * Outer board mode. In 'draw' mode, pointer-down on this token starts
   * an arrow at the token's position instead of a drag.
   */
  mode: 'move' | 'draw';
  /** Override position (run-path playback). Falls back to spot when null. */
  playbackPos?: { x: number; z: number } | null;
  onSelect: () => void;
  onDragChange: (dragging: boolean) => void;
  onMove: (next: { x: number; z: number }) => void;
  /** In 'draw' mode, the coach pressed on this token — start an arrow. */
  onStartArrowFromToken?: (origin: { x: number; z: number }, playerKey: string) => void;
  /** Stable key used by the parent (e.g. "home-3"). */
  playerKey: string;
  /** Provides the parent <svg> — needed for client→world coordinate mapping. */
  svgRef: React.RefObject<SVGSVGElement | null>;
}

const HOME_FILL = '#4ade80';
const AWAY_FILL = '#fbbf24';
const HOME_STROKE = '#22c55e';
const AWAY_STROKE = '#f59e0b';
// Tokens read at ~2.4m diameter from a 105m-long pitch — large enough to
// touch on a touchline phone without crowding 11v11 layouts.
const TOKEN_RADIUS = 2;
const ROLE_OFFSET = 4.6;

/**
 * Map a pointer event into world (svg user-space) metres. Caller passes
 * the parent <svg>; we use its inverse CTM to convert clientX/Y back.
 * Falls back to the raw client delta if the CTM is missing (which only
 * happens in headless test environments without layout).
 *
 * The 2D pitch renders LANDSCAPE: SVG-x = -world.z, SVG-y = world.x.
 * Reversing the mapping for the inverse CTM result gives us back world
 * coordinates that match the persistence model (the 3D board uses the
 * same world-XZ metres in `tactics-3d-${id}`).
 */
function clientToWorld(
  svg: SVGSVGElement | null,
  clientX: number,
  clientY: number,
): { x: number; z: number } | null {
  if (!svg) return null;
  // Some test environments (happy-dom) don't implement getScreenCTM.
  // Bail out so the parent can still render — drag is a no-op there
  // but the structural tests we run against the 2D board never need it.
  const ctm =
    typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
  if (!ctm) return null;
  // SVGPoint via createSVGPoint is the canonical way; for browsers that
  // dropped it (TS dom lib types are conservative) we fall back to a
  // DOMPoint, which all evergreen engines ship.
  const pt =
    typeof svg.createSVGPoint === 'function'
      ? svg.createSVGPoint()
      : new DOMPoint();
  pt.x = clientX;
  pt.y = clientY;
  const inverse = ctm.inverse();
  const transformed =
    'matrixTransform' in pt && typeof (pt as SVGPoint).matrixTransform === 'function'
      ? (pt as SVGPoint).matrixTransform(inverse)
      : (pt as DOMPoint).matrixTransform(inverse);
  // SVG (x, y) → world (y_svg, -x_svg). Inverse of the landscape mapping.
  return { x: transformed.y, z: -transformed.x };
}

function PlayerToken2DInner({
  spot,
  number,
  label,
  team,
  selected,
  mode,
  playbackPos,
  onSelect,
  onDragChange,
  onMove,
  onStartArrowFromToken,
  playerKey,
  svgRef,
}: PlayerToken2DProps) {
  const draggingRef = useRef(false);
  // Live drag offset in world metres. We mutate the wrapper <g>'s
  // `transform` attribute directly via this ref on every pointer-move
  // so React never re-renders the parent until pointer-up.
  const dragOffsetRef = useRef<{ dx: number; dz: number }>({ dx: 0, dz: 0 });
  const groupRef = useRef<SVGGElement | null>(null);

  const fill = team === 'home' ? HOME_FILL : AWAY_FILL;
  const stroke = team === 'home' ? HOME_STROKE : AWAY_STROKE;
  const numberColor = team === 'home' ? '#04140b' : '#1a1410';

  // Counter-scale every dimension by the pitch zoom so the token holds
  // a constant on-screen size — a set-piece zoom must not bloat the
  // roundels into giant discs. 1 at full pitch, smaller when zoomed.
  const z = useContext(PitchZoomContext);
  const radius = TOKEN_RADIUS * z;
  const ringRadius = (TOKEN_RADIUS + 0.8) * z;
  const ringStroke = 0.4 * z;
  const bodyStroke = 0.3 * z;
  const numberSize = (label && label.length > 2 ? 1.7 : 2.2) * z;
  const numberDy = 0.7 * z;
  const roleSize = 1.6 * z;
  const roleOffset = ROLE_OFFSET * z;

  // Landscape mapping: SVG-x = -world.z, SVG-y = world.x.
  const worldX = playbackPos ? playbackPos.x : spot.x;
  const worldZ = playbackPos ? playbackPos.z : spot.z;
  const cx = -worldZ;
  const cy = worldX;

  // Clear any leftover drag transform when the parent commits a new
  // `spot` — the visible position now matches the data and the offset
  // should reset to zero.
  useEffect(() => {
    dragOffsetRef.current = { dx: 0, dz: 0 };
    if (groupRef.current) {
      groupRef.current.removeAttribute('transform');
    }
  }, [spot.x, spot.z]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      if (mode === 'draw') {
        e.stopPropagation();
        onStartArrowFromToken?.({ x: spot.x, z: spot.z }, playerKey);
        return;
      }
      e.stopPropagation();
      onSelect();
      draggingRef.current = true;
      dragOffsetRef.current = { dx: 0, dz: 0 };
      onDragChange(true);
      // Capture on the outer group so move/up keep firing on this <g>
      // regardless of where the cursor strays inside the SVG.
      const cap = e.currentTarget;
      cap.setPointerCapture?.(e.pointerId);
    },
    [mode, onDragChange, onSelect, onStartArrowFromToken, playerKey, spot.x, spot.z],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      if (!draggingRef.current || mode !== 'move') return;
      const next = clientToWorld(svgRef.current, e.clientX, e.clientY);
      if (!next) return;
      const dx = next.x - spot.x;
      const dz = next.z - spot.z;
      dragOffsetRef.current = { dx, dz };
      if (groupRef.current) {
        // World (dx, dz) → SVG (-dz, dx) in our landscape mapping, so
        // the visual translate matches the cursor's path through world
        // space.
        groupRef.current.setAttribute('transform', `translate(${-dz} ${dx})`);
      }
    },
    [mode, svgRef, spot.x, spot.z],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<SVGGElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      onDragChange(false);
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      const { dx, dz } = dragOffsetRef.current;
      // Only commit if the user actually moved — saves a state update
      // (and a parent re-render) on a pure tap-to-select.
      if (dx !== 0 || dz !== 0) {
        onMove({ x: spot.x + dx, z: spot.z + dz });
      }
      // Visuals stay where they were dropped until the effect above
      // reconciles on the new `spot`. If the parent clamps the position,
      // the resulting prop change re-triggers the reset effect.
    },
    [onDragChange, onMove, spot.x, spot.z],
  );

  return (
    <g
      ref={groupRef}
      className="t2d-token"
      data-testid={`t2d-token-${playerKey}`}
      data-team={team}
      data-selected={selected || undefined}
      data-role={spot.role}
      role="img"
      aria-label={`${team === 'home' ? 'Home' : 'Away'} #${number} — ${spot.role}${selected ? ' (selected)' : ''}`}
      aria-pressed={selected}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ cursor: mode === 'draw' ? 'crosshair' : 'grab' }}
    >
      {/* Broadcast focus ring — pulsing yellow stroke around the
          selected player. NFL telestrator / Sky-MNF idiom: the ring
          breathes between scale 1 and 1.25 / opacity 0.85→0.35 so the
          coach's eye snaps to the focused player.
          Pulse runs via CSS keyframes on `.t2d-focus-ring`; reduced
          motion falls back to a static visible ring. */}
      {selected && (
        <circle
          className="t2d-focus-ring"
          cx={cx}
          cy={cy}
          r={ringRadius}
          fill="none"
          stroke="rgba(251, 191, 36, 0.95)"
          strokeWidth={ringStroke}
          pointerEvents="none"
        />
      )}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={fill}
        stroke={stroke}
        strokeWidth={bodyStroke}
      />
      <text
        x={cx}
        y={cy + numberDy}
        textAnchor="middle"
        fontSize={numberSize}
        fontWeight={700}
        fill={numberColor}
        pointerEvents="none"
        style={{ userSelect: 'none' }}
      >
        {label ?? number}
      </text>
      {/* Role chip floats above the token when selected (mirrors 3D). */}
      {selected && (
        <text
          x={cx}
          y={cy - roleOffset}
          textAnchor="middle"
          fontSize={roleSize}
          fill={team === 'home' ? '#bbf7d0' : '#fde68a'}
          pointerEvents="none"
          style={{
            textTransform: 'uppercase',
            letterSpacing: 0.2,
            userSelect: 'none',
          }}
        >
          {spot.role}
        </text>
      )}
    </g>
  );
}

/**
 * React.memo so a single token's drag doesn't re-render all 22 figures.
 * The default shallow compare matches the props' identity-stable shape
 * (callbacks should be wrapped in useCallback by the parent — verified
 * for TacticsBoard2D's moveSpot / onSelect / onDragChange chain).
 */
export const PlayerToken2D = memo(PlayerToken2DInner);
